import { getContractOptionsForDomain, MEDICAL_CONTRACT_OPTIONS, STANDARD_CONTRACT_OPTIONS } from "../form-config";
import type { FieldOption, IntakeStep } from "../types";
import { createDoctorProfile, createProductOption, FIXED_UPSELL_COMMISSION_RATE } from "./default-data";
import { GENERAL_GUIDANCE_DEFAULTS } from "./guidance-defaults";
import { DOCUMENT_UPLOADS, PHOTO_UPLOADS, referenceSpotUrls } from "./uploads";

const AVAILABLE_LANGUAGE_OPTIONS: FieldOption[] = [
  { id: "option.language.availableLanguages.base.english", label: "영어", value: "language:영어", description: "base" },
  { id: "option.language.availableLanguages.base.japanese", label: "일본어", value: "language:일본어", description: "base" },
  { id: "option.language.availableLanguages.base.chinese", label: "중국어", value: "language:중국어", description: "base" },
  { id: "option.language.availableLanguages.other.thai", label: "태국어", value: "language:태국어", description: "other" },
  { id: "option.language.availableLanguages.other.vietnamese", label: "베트남어", value: "language:베트남어", description: "other" },
  { id: "option.language.availableLanguages.other.spanish", label: "스페인어", value: "language:스페인어", description: "other" },
  { id: "option.language.availableLanguages.other.french", label: "프랑스어", value: "language:프랑스어", description: "other" },
  { id: "option.language.availableLanguages.other.german", label: "독일어", value: "language:독일어", description: "other" },
  { id: "option.language.availableLanguages.other.russian", label: "러시아어", value: "language:러시아어", description: "other" },
  { id: "option.language.availableLanguages.other.arabic", label: "아랍어", value: "language:아랍어", description: "other" },
  { id: "option.language.availableLanguages.other.indonesian", label: "인도네시아어", value: "language:인도네시아어", description: "other" },
  { id: "option.language.availableLanguages.other.malay", label: "말레이어", value: "language:말레이어", description: "other" },
  { id: "option.language.availableLanguages.other.hindi", label: "힌디어", value: "language:힌디어", description: "other" },
  { id: "option.language.availableLanguages.other.tamil", label: "타밀어", value: "language:타밀어", description: "other" },
  { id: "option.language.availableLanguages.other.bengali", label: "벵골어", value: "language:벵골어", description: "other" },
  { id: "option.language.availableLanguages.other.urdu", label: "우르두어", value: "language:우르두어", description: "other" },
  { id: "option.language.availableLanguages.other.nepali", label: "네팔어", value: "language:네팔어", description: "other" },
  { id: "option.language.availableLanguages.other.sinhala", label: "싱할라어", value: "language:싱할라어", description: "other" },
  { id: "option.language.availableLanguages.other.mongolian", label: "몽골어", value: "language:몽골어", description: "other" },
  { id: "option.language.availableLanguages.other.turkish", label: "튀르키예어", value: "language:튀르키예어", description: "other" },
  { id: "option.language.availableLanguages.other.italian", label: "이탈리아어", value: "language:이탈리아어", description: "other" },
  { id: "option.language.availableLanguages.other.portuguese", label: "포르투갈어", value: "language:포르투갈어", description: "other" },
  { id: "option.language.availableLanguages.other.dutch", label: "네덜란드어", value: "language:네덜란드어", description: "other" },
  { id: "option.language.availableLanguages.other.swedish", label: "스웨덴어", value: "language:스웨덴어", description: "other" },
  { id: "option.language.availableLanguages.other.polish", label: "폴란드어", value: "language:폴란드어", description: "other" },
  { id: "option.language.availableLanguages.other.ukrainian", label: "우크라이나어", value: "language:우크라이나어", description: "other" },
  { id: "option.language.availableLanguages.other.kazakh", label: "카자흐어", value: "language:카자흐어", description: "other" },
  { id: "option.language.availableLanguages.other.filipino", label: "필리핀어", value: "language:필리핀어", description: "other" },
  { id: "option.language.availableLanguages.other.khmer", label: "크메르어", value: "language:크메르어", description: "other" },
  { id: "option.language.availableLanguages.other.myanmar", label: "미얀마어", value: "language:미얀마어", description: "other" },
  { id: "option.language.availableLanguages.status.resident", label: "상주", value: "status:상주", description: "status" },
  { id: "option.language.availableLanguages.status.reservation", label: "예약 시 배정 가능", value: "status:예약 시 배정 가능", description: "status" },
  { id: "option.language.availableLanguages.status.simple", label: "간단 응대만 가능", value: "status:간단 응대만 가능", description: "status" },
];

const CAUTION_LANGUAGE_LABELS = ["영어", "일본어", "중국어", "태국어", "베트남어", "인도네시아어", "스페인어", "프랑스어", "독일어", "러시아어"];

const CAUTION_LANGUAGE_OPTIONS: FieldOption[] = CAUTION_LANGUAGE_LABELS
  .map((label) => AVAILABLE_LANGUAGE_OPTIONS.find((option) => option.label === label))
  .filter((option): option is FieldOption => Boolean(option))
  .map((option) => ({
    id: option.id?.replace("availableLanguages", "cautionLanguages"),
    label: option.label,
    value: option.label,
  }));

export const REVIEW_BENEFIT_HELPER_TEXT = "리뷰가 적으면 구매 전환이 낮아질 수 있어요. 자연스럽게 리뷰를 받을 혜택을 추천합니다.";

export function getBookingQuestionChecklistHelperText(categoryLabel: string): string {
  return `${categoryLabel} 예약에 꼭 필요한 정보만 선택해주세요. 너무 많은 정보를 요청하면 예약 과정이 길어져 고객 이탈률이 높아질 수 있어요.`;
}

export const INTAKE_STEPS: IntakeStep[] = [
  {
    id: "welcome",
    eyebrow: "크리에이트립 입점을 위한",
    title: "상품요청서를 작성해주세요",
    description: "상품 페이지 제작에 필요한 정보를 입력해주세요.",
  },
  {
    id: "company",
    eyebrow: "Step 1",
    title: "업체 기본 정보를 입력해주세요",
    description: "상품 페이지와 예약 운영에 필요한 정보입니다.",
    fields: [
      { id: "storeNameKo", path: "company.storeNameKo", label: "상점명 / 병원명(한국어)", type: "text", required: true, placeholder: "예: 00의원", copyKind: "placeholder" },
      { id: "storeNameEn", path: "company.storeNameEn", label: "상점명 / 병원명(영어)", type: "text", placeholder: "영문 표기가 있다면 입력해주세요", copyKind: "placeholder" },
      { id: "storeNameJa", path: "company.storeNameJa", label: "상점명 / 병원명(일본어)", type: "text", placeholder: "일본어 표기가 있다면 입력해주세요", copyKind: "placeholder" },
      { id: "storeNameZh", path: "company.storeNameZh", label: "상점명 / 병원명(중국어)", type: "text", placeholder: "중국어 표기가 있다면 입력해주세요", copyKind: "placeholder" },
      { id: "address", path: "company.address", label: "주소", type: "textarea", required: true, rows: 2, placeholder: "예: 서울 마포구 양화로 000", copyKind: "placeholder" },
      { id: "representativePhone", path: "company.representativePhone", label: "업체 대표 전화번호", type: "tel", placeholder: "예: 02-000-0000", helperText: "카카오맵에서 찾은 대표번호를 참고용으로 채울 수 있습니다. 담당자 연락처와 다를 수 있어요.", copyKind: "helper" },
      { id: "kakaoMapsUrl", path: "company.kakaoMapsUrl", label: "카카오맵 링크", type: "url", placeholder: "https://place.map.kakao.com/...", helperText: "카카오맵 장소 조회 결과로 자동 채울 수 있습니다.", copyKind: "helper" },
      { id: "kakaoCategory", path: "company.kakaoCategory", label: "카카오맵 분류", type: "text", placeholder: "예: 의료,건강 > 병원", helperText: "카카오맵 검색 결과의 업종 분류입니다.", copyKind: "helper", defaultHidden: true },
      { id: "googleMapsUrl", path: "company.googleMapsUrl", label: "구글맵 링크", type: "url", placeholder: "https://maps.google.com/...", helperText: "구글맵 리뷰나 온라인 예약 버튼 연동을 검토할 때 사용합니다.", copyKind: "helper" },
      { id: "websiteUrl", path: "company.websiteUrl", label: "공식 홈페이지", type: "url", placeholder: "https://..." },
      { id: "instagramUrl", path: "company.instagramUrl", label: "인스타그램 / SNS", type: "url", placeholder: "https://instagram.com/..." },
      { id: "contentReferenceUrl", path: "company.contentReferenceUrl", label: "기타 콘텐츠에 참고할 URL (선택)", type: "textarea", rows: 2, placeholder: "공식 사이트, 기존 상품 페이지, 소개서/브로슈어 링크 등", helperText: "상품 페이지 작성에 참고하면 좋은 링크가 있다면 입력해주세요.", copyKind: "helper", defaultHidden: true },
      { id: "contactName", path: "company.contactName", label: "업체 담당자명", type: "text", required: true, placeholder: "예: 김00", copyKind: "placeholder" },
      { id: "contactRole", path: "company.contactRole", label: "담당자 직함", type: "text", placeholder: "예: 팀장, 실장, 원장", defaultHidden: true },
      { id: "contactDepartment", path: "company.contactDepartment", label: "담당자 부서", type: "text", placeholder: "예: 마케팅팀, 상담실, 경영지원", defaultHidden: true },
      { id: "contactEmail", path: "company.contactEmail", label: "담당자 이메일", type: "email", required: true, placeholder: "abcd@example.com", copyKind: "placeholder" },
      { id: "contactPhone", path: "company.contactPhone", label: "담당자 연락처", type: "tel", required: true, placeholder: "010-0000-0000", copyKind: "placeholder" },
      { id: "useContactEmailForNotifications", path: "reservation.useContactEmailForNotifications", label: "예약 알림 이메일", type: "checkbox", checkboxText: "담당자 이메일로 예약 알림을 받을게요", helperText: "예약 접수/확정/취소 시 자동으로 메일이 발송됩니다." },
      { id: "notificationEmails", path: "reservation.notificationEmails", label: "예약 알림 이메일 별도 입력", type: "textarea", rows: 2, placeholder: "예: reservation@example.com / 여러 명이면 쉼표 또는 줄바꿈으로 입력", helperText: "담당자 이메일과 다른 주소로 받고 싶을 때 입력해주세요.", copyKind: "helper" },
      { id: "useContactPhoneForAlimtalk", path: "reservation.useContactPhoneForAlimtalk", label: "예약 알림톡", type: "checkbox", checkboxText: "담당자 연락처로 예약 알림톡을 받을게요", helperText: "예약 접수 시 자동으로 알림톡이 발송됩니다." },
      { id: "notificationPhones", path: "reservation.notificationPhones", label: "알림톡 받을 번호 별도 입력", type: "textarea", rows: 2, placeholder: "예: 010-0000-0000 / 여러 명이면 쉼표 또는 줄바꿈으로 입력", helperText: "담당자 연락처와 다른 번호로 받고 싶을 때 입력해주세요.", copyKind: "helper" },
      { id: "useContactInfoForTaxInvoice", path: "company.useContactInfoForTaxInvoice", label: "세금계산서 (정산 담당자)", type: "checkbox", checkboxText: "일반 담당자 정보와 동일해요", helperText: "세금계산서 발행 및 정산 관련 연락처입니다." },
      { id: "taxInvoiceContactName", path: "company.taxInvoiceContactName", label: "세금계산서 담당자명", type: "text", required: true, placeholder: "예: 김00", copyKind: "placeholder" },
      { id: "taxInvoiceContactPhone", path: "company.taxInvoiceContactPhone", label: "세금계산서 담당자 연락처", type: "tel", required: true, placeholder: "010-0000-0000", copyKind: "placeholder" },
      { id: "taxInvoiceContactEmail", path: "company.taxInvoiceContactEmail", label: "세금계산서 담당자 이메일", type: "email", required: true, placeholder: "billing@example.com", copyKind: "placeholder" },
    ],
  },
  {
    id: "reservation",
    eyebrow: "Step 2",
    title: "예약 운영 정보를 입력해주세요",
    description: "파란 기본값이 실제와 다른 곳만 수정해주세요.",
    fields: [
      { id: "bookableStartOffsetDays", path: "reservation.bookableStartOffsetDays", label: "며칠 뒤 예약부터 받을까요?", type: "number", required: true, helperText: "기본값 2일. 0 입력 시 당일예약도 가능합니다.", defaultValue: 2, copyKind: "default" },
      { id: "bookableWindowMonths", path: "reservation.bookableWindowMonths", label: "몇 달치 예약을 열까요?", type: "number", required: true, helperText: "기본값 2개월. 매일 하루씩 자동 오픈됩니다.", defaultValue: 2, copyKind: "default" },
      { id: "businessHours", path: "reservation.businessHours", label: "업체 영업시간", type: "textarea", required: true, rows: 2, placeholder: "예: 월~토 09:00~20:00 / 점심시간 12:00~13:00", helperText: "고객에게 안내되는 전체 영업시간입니다. 기본값이 실제와 다르면 고쳐주세요.", defaultValue: "월~토 09:00~20:00 / 점심시간 12:00~13:00", copyKind: "default" },
      { id: "reservationHours", path: "reservation.reservationHours", label: "크리에이트립 예약 가능 시간", type: "textarea", required: true, rows: 2, placeholder: "예: 월~토 09:00~11:30 / 13:00~19:30", helperText: "영업시간 중 크리에이트립 고객 예약을 받을 시간입니다. 예: 09:00~18:00은 18:00까지 시술·주문 등을 시작하는 고객을 받겠다는 의미예요.", defaultValue: "월~토 09:00~11:30 / 13:00~19:30", copyKind: "default" },
      {
        id: "reservationUnit",
        path: "reservation.reservationUnit",
        label: "예약 간격",
        type: "select",
        required: true,
        helperText: "고객이 예약할 때 선택할 수 있는 시간의 간격입니다. 예: 30분간격으로 설정하면 1:00, 1:30, 2:00 .. 처럼 30분 간격 선택지가 열리게 됩니다.",
        defaultValue: "1시간",
        options: [
          { label: "15분", value: "15분" },
          { label: "30분", value: "30분" },
          { label: "1시간", value: "1시간" },
          { label: "1시간 30분", value: "1시간 30분" },
          { label: "2시간", value: "2시간" },
        ],
        copyKind: "default",
      },
      {
        id: "confirmationMode",
        path: "reservation.confirmationMode",
        label: "예약 접수 후 확인이 필요할까요?",
        type: "radio",
        required: true,
        helperText: "기본값은 ‘승인대기 후 확정’입니다. 예약 접수 → 파트너 확인 → 예약확정 순서로 진행됩니다.",
        options: [
          { label: "네, 확인 후 확정", value: "approval_required", description: "예약 가능 여부를 확인하고 확정합니다. 대부분의 예약형 상품에 추천합니다." },
          { label: "아니요, 바로 자동확정", value: "auto_confirm", description: "예약 시간만 비어 있으면 별도 확인 없이 바로 확정합니다." },
        ],
        defaultValue: "approval_required",
        copyKind: "reference",
      },
      {
        id: "communicationChannel",
        path: "reservation.communicationChannel",
        label: "예약 확인은 어디로 받을까요?",
        type: "select",
        helperText: "기본값은 메일입니다. 실시간 확인이 필요하면 카카오톡 단톡방이나 Admin을 선택해주세요.",
        options: [
          { label: "메일", value: "email", description: "예약 내역을 이메일로 받고 회신합니다." },
          { label: "카카오톡 단톡방", value: "kakao", description: "크리에이트립 담당자와 파트너 담당자가 함께 실시간으로 확인합니다." },
          { label: "크리에이트립 Admin", value: "creatrip_admin", description: "예약 내역을 직접 확인하고 관리할 수 있습니다." },
          { label: "소통 채널 불필요", value: "none", description: "자동확정으로 운영할 때 선택합니다." },
        ],
        defaultValue: "email",
        copyKind: "helper",
      },
      { id: "adminAccountNeeded", path: "reservation.adminAccountNeeded", label: "크리에이트립 Admin 계정", type: "checkbox", checkboxText: "예약 확인/처리를 위한 Admin 계정을 지금 만들게요", helperText: "예약 접수/취소/변경 내역을 확인하고 직접 처리할 수 있는 계정입니다.", copyKind: "helper", defaultHidden: true },
      { id: "adminLoginId", path: "reservation.adminLoginId", label: "희망 Admin ID", type: "text", required: true, placeholder: "예: partner-reservation", helperText: "희망하시는 Admin ID를 입력해주세요.", defaultHidden: true },
      { id: "adminPassword", path: "reservation.adminPassword", label: "희망 Admin 임시 PW (선택)", type: "password", placeholder: "비워두면 담당자가 별도로 안내합니다", helperText: "지금 정하지 않아도 됩니다. 비워두면 크리에이트립 담당자가 계정 생성 과정에서 별도로 안내드릴게요.", defaultHidden: true },
    ],
    tips: [
      { title: "기본값 그대로 시작해도 괜찮아요", body: "실제 영업시간만 맞게 확인해주세요.", kind: "info" },
    ],
  },
  {
    id: "contract",
    eyebrow: "Step 3",
    // 계약 조건은 별도 협의로 진행 — 기본 숨김, 필요 시 어드민 문항 설정에서 노출
    defaultHidden: true,
    title: "계약 / 예약 구조를 선택해주세요",
    description: "계약 구조는 아래 두 가지 중 하나를 선택합니다. 초반 예약 유도 효과를 우선하면 ‘무료예약 + 업셀링’, 노쇼 방지가 더 중요하면 ‘보증금 결제 + 업셀링’을 권장합니다.",
    fields: [
      {
        id: "contractType",
        path: "contract.contractType",
        label: "계약 구조",
        type: "radio",
        required: true,
        options: MEDICAL_CONTRACT_OPTIONS,
        copyKind: "helper",
      },
      { id: "upsellCommissionRate", path: "contract.upsellCommissionRate", label: "업셀링 수수료율 (%)", type: "number", min: 0.01, max: 100, step: 0.01, helperText: "현장에서 실제로 받는 상품/서비스 금액 기준으로 업셀링 수수료를 수취합니다. 협의된 수수료율이 있다면 수정해주세요. 최종 수수료율은 계약 과정에서 확정됩니다. 부가세 별도입니다.", defaultValue: FIXED_UPSELL_COMMISSION_RATE, copyKind: "default" },
      { id: "depositPolicyMemo", path: "contract.depositPolicyMemo", label: "보증금 관련 요청사항 (선택)", type: "textarea", rows: 4, placeholder: "예: 특정 상품은 보증금 20,000원으로 설정하고 싶어요 / 기본 정책 그대로 진행", helperText: "기본 보증금 정책대로 진행하면 비워두셔도 됩니다.", copyKind: "helper" },
      { id: "settlementMemo", path: "contract.settlementMemo", label: "정산 관련 특이사항 (선택)", type: "textarea", rows: 3, placeholder: "예: 정산서/세금계산서 담당자가 별도로 있어요 / 기본 월 1회 후정산 그대로 진행", helperText: "기본 월 1회 후정산이면 비워두셔도 됩니다.", copyKind: "helper" },
    ],
    tips: [
      { title: "공통 수수료와 정산", body: "• 현장에서 실제로 받는 상품/서비스 금액 기준 업셀링 수수료 10% 적용\n• 정산은 월 1회 후정산으로 진행\n• 파트너 측에서 1일~말일 내역을 익월 초 정산서로 발행", kind: "info" },
      { title: "무료예약 구조", body: "• 예약 진입 장벽이 낮아 예약 유도 효과가 큼\n• 현장 응대 시 보증금 차감이 필요 없어 편리함\n• 노쇼 가능성이 있어 필요 시 방지 방안 논의 가능", kind: "success" },
    ],
  },
  {
    id: "products",
    eyebrow: "Step 4",
    title: "예약 상품을 입력해주세요",
    description: "판매할 핵심 상품의 구성과 장점을 입력해주세요.",
    repeaters: [
      {
        id: "products",
        path: "products",
        label: "상품 옵션",
        description: "엑셀처럼 행과 열로 입력하거나, 스프레드시트에서 복사한 값을 붙여넣을 수 있어요.",
        addLabel: "상품 추가",
        minItems: 1,
        createItem: createProductOption,
        fields: [
          { id: "optionName", path: "optionName", label: "옵션명", type: "text", required: true, placeholder: "예: 미백 및 광조사 3회", helperText: "무료예약 또는 보증금 상담 상품도 유지하는 것을 추천합니다.", copyKind: "placeholder" },
          { id: "listPrice", path: "listPrice", label: "정가", type: "currency", placeholder: "예: 600,000원" },
          { id: "salePrice", path: "salePrice", label: "판매가", type: "currency", placeholder: "예: 500,000원", helperText: "크리에이트립은 원화(KRW) 기준으로 판매가를 설정합니다.", copyKind: "helper" },
          { id: "depositOrSettlementPrice", path: "depositOrSettlementPrice", label: "보증금 / 정산가", type: "text", placeholder: "예: 무료 또는 보증금 20,000원" },
          { id: "freeReservationLabel", path: "freeReservationLabel", label: "무료예약", type: "text", placeholder: "무료예약" },
          { id: "depositAmount", path: "depositAmount", label: "보증금", type: "currency", placeholder: "예: 20,000원" },
          { id: "settlementPrice", path: "settlementPrice", label: "정산가", type: "currency", placeholder: "예: 400,000원" },
          { id: "maxQuantityPerSlot", path: "maxQuantityPerSlot", label: "한 타임 예약가능 최대수량", type: "text", placeholder: "예: 1명 / 2명" },
          { id: "duration", path: "duration", label: "소요시간", type: "text", required: true, placeholder: "예: 약 60분", helperText: "상담·준비·실제 이용을 포함해 고객이 현장에 머무는 시간을 적어주세요." },
          { id: "includedItems", path: "includedItems", label: "포함 항목", type: "textarea", rows: 3, placeholder: "상품에 포함되는 상담, 검사, 시술, 관리 등을 적어주세요" },
          { id: "excludedItems", path: "excludedItems", label: "제외 항목", type: "textarea", rows: 3, placeholder: "현장 추가 결제, 부가세, 약값 등 포함되지 않는 항목이 있다면 적어주세요", defaultHidden: true },
          { id: "recommendationReason", path: "recommendationReason", label: "이 상품을 추천하는 이유", type: "textarea", required: true, rows: 4, placeholder: "예:\n- 어떤 고객에게 특히 잘 맞는지\n- 다른 옵션보다 좋은 점은 무엇인지\n- 외국인 고객이 안심할 수 있는 이유", helperText: "고객의 상황과 얻을 수 있는 결과를 연결해 2~3문장 이상 적어주세요." },
          { id: "detailDescription", path: "detailDescription", label: "상품 상세 설명", type: "textarea", required: true, rows: 5, placeholder: "예:\n1. 상담 및 상태 확인\n2. 상품/서비스 진행\n3. 마무리 안내\n\n사용 장비·재료, 진행 방식, 예상 결과도 함께 적어주세요.", helperText: "대괄호 안을 실제 진행 순서와 안내 내용으로 바꿔주세요.", defaultValue: GENERAL_GUIDANCE_DEFAULTS.consultationProduct.detailDescription, copyKind: "default" },
          { id: "effectDuration", path: "effectDuration", label: "효과 지속 기간", type: "text", placeholder: "예: 개인차 있음 / 약 6개월", defaultHidden: true },
          { id: "recommendedTargets", path: "recommendedTargets", label: "추천 대상", type: "textarea", rows: 3, placeholder: "어떤 고객에게 추천하는지 적어주세요", defaultHidden: true },
          { id: "productPrecautions", path: "productPrecautions", label: "상품별 주의사항", type: "textarea", rows: 3, placeholder: "상품별로 꼭 안내해야 하는 주의사항" },
        ],
      },
    ],
    tips: [
      { title: "상담 상품 판매 추천", body: "외국인 고객들은 실제 어떤 시술을 받을지 정하지 못한 경우가 많아 현장에서 상담 후 결정하기 원합니다. ‘무료예약 | 상담 후 시술 결정’ 상품은 유지하는 것을 추천합니다.", kind: "success" },
      { title: "크리에이트립 단독 구성", body: "크리에이트립에서만 예약할 수 있는 단독 패키지, 단독 할인가, 단독 혜택이 있다면 적극적으로 적어주세요.\n예: [크리에이트립 단독 패키지] aa+bb+cc: 정가 600,000원 / 판매가 500,000원", kind: "info" },
    ],
  },
  {
    id: "content",
    eyebrow: "Step 5",
    title: "업체의 장점과 혜택을 입력해주세요",
    description: "외국인 고객이 예약할 이유를 실제 사례와 함께 알려주세요.",
    fields: [
      { id: "catchphrase", path: "content.catchphrase", label: "상품 제목에 붙는 짧은 소개 문구", type: "text", required: true, placeholder: "예: 하루 만에 완성하는 특별한 경험", helperText: "대괄호 안을 실제 상품과 장점으로 바꿔주세요.", defaultValue: GENERAL_GUIDANCE_DEFAULTS.content.catchphrase, copyKind: "default" },
      {
        id: "sellingPointTags",
        path: "content.sellingPointTags",
        label: "우리 업체 장점 고르기",
        type: "multiCheckbox",
        required: true,
        helperText: "해당되는 장점을 모두 골라주세요. 2개 이상 고르면 상품 페이지의 핵심 메시지를 잡기 좋아요.",
        options: [
          { label: "외국어 응대 가능", value: "외국어 응대 가능" },
          { label: "원장/전문의 직접 상담", value: "원장/전문의 직접 상담" },
          { label: "위치 접근성 좋음", value: "위치 접근성 좋음" },
          { label: "야간/주말 진료 가능", value: "야간/주말 진료 가능" },
          { label: "관광객 방문 편의", value: "관광객 방문 편의" },
          { label: "가격/패키지 경쟁력", value: "가격/패키지 경쟁력" },
          { label: "크리에이트립 단독 혜택 있음", value: "크리에이트립 단독 혜택 있음" },
        ],
      },
      {
        id: "touristConvenienceOptions",
        path: "content.touristConvenienceOptions",
        label: "관광객 방문 편의 상세",
        type: "multiCheckbox",
        helperText: "제공 가능한 편의 항목을 선택해주세요.",
        options: [
          { label: "짐 보관", value: "짐 보관" },
          { label: "외국어 안내문", value: "외국어 안내문" },
          { label: "와이파이 / 충전", value: "와이파이 / 충전" },
          { label: "해외카드 결제", value: "해외카드 결제" },
        ],
      },
      { id: "primarySellingPoint", path: "content.primarySellingPoint", label: "가장 강하게 어필하고 싶은 장점", type: "textarea", required: true, rows: 5, placeholder: "예:\n- 대표 상품과 고객이 얻는 결과\n- 경력·누적 이용 건수·후기 수 등 신뢰할 숫자\n- 외국인 고객에게 특히 편리한 점", helperText: "대괄호 안을 실제 장점과 근거로 바꿔주세요.", defaultValue: GENERAL_GUIDANCE_DEFAULTS.content.primarySellingPoint, copyKind: "default" },
      {
        id: "targetCustomers",
        path: "content.targetCustomers",
        label: "어떤 고객에게 추천하나요?",
        type: "multiCheckbox",
        required: true,
        helperText: "여러 개를 골라도 됩니다. 가장 잘 맞는 고객을 고르고, 특정 고민이나 국가는 아래 추천 이유에 자세히 적어주세요.",
        options: [
          { label: "관광객", value: "관광객" },
          { label: "유학생", value: "유학생" },
          { label: "장기체류자", value: "장기체류자" },
          { label: "가격 비교 고객", value: "가격 비교 고객" },
          { label: "프리미엄 시술 관심 고객", value: "프리미엄 시술 관심 고객" },
          { label: "특정 고민이 있는 고객", value: "특정 고민이 있는 고객" },
        ],
      },
      { id: "targetCustomerConcernDetail", path: "content.targetCustomerConcernDetail", label: "특정 고민 상세", type: "text", placeholder: "예: 여드름 흉터, 치아 미백, 리프팅, 피부톤 개선", helperText: "어떤 고민을 가진 고객에게 특히 추천하는지 적어주세요." },
      {
        id: "availableLanguages",
        path: "language.availableLanguages",
        label: "가능 외국어 및 응대 방식",
        type: "text",
        required: true,
        helperText: "가능한 언어마다 상주/예약 시 배정/간단 응대 여부를 선택해주세요. 실제 고객 응대가 가능한 수준으로 선택해주세요.",
        options: AVAILABLE_LANGUAGE_OPTIONS,
      },
      {
        id: "cautionLanguages",
        path: "language.cautionLanguages",
        label: "서면 주의사항 제공 언어",
        type: "multiCheckbox",
        required: true,
        helperText: "고객에게 주의사항을 서면(문서·안내문)으로 제공할 수 있는 언어를 모두 선택해주세요. 통역 가능 언어가 아니라 서면 안내 제공 언어를 선택합니다.",
        options: CAUTION_LANGUAGE_OPTIONS,
      },
      { id: "recommendationReason", path: "content.recommendationReason", label: "우리 업체를 외국인 고객에게 추천할 수 있는 이유", type: "textarea", required: true, rows: 7, placeholder: "2~3문장 이상 적어주세요.\n예:\n1. 어떤 외국인 고객에게 추천하나요?\n2. 고객이 여기서만 얻을 수 있는 경험·결과는 무엇인가요?\n3. 이를 증명할 경력, 후기, 숫자, 운영 사례가 있나요?", helperText: "대괄호 안을 실제 사례와 숫자로 바꿔주세요.", defaultValue: GENERAL_GUIDANCE_DEFAULTS.content.recommendationReason, copyKind: "default" },
      {
        id: "creatripExclusiveBenefitStatus",
        path: "content.creatripExclusiveBenefitStatus",
        label: "크리에이트립 단독 혜택 여부",
        type: "radio",
        required: true,
        defaultValue: "있음",
        helperText: "기본값은 ‘있음’입니다. 제공 가능한 단독 패키지, 단독 할인가, 단독 혜택을 적극적으로 검토해주세요.",
        options: [
          { label: "있음", value: "있음" },
          { label: "없음", value: "없음" },
          { label: "준비/상담 중", value: "준비/상담 중" },
        ],
      },
      {
        id: "creatripExclusiveBenefit",
        path: "content.creatripExclusiveBenefit",
        label: "크리에이트립 단독 혜택 상세",
        type: "textarea",
        required: true,
        rows: 8,
        placeholder: "예:\n- 단독 할인: 정상가 100,000원 → 크리에이트립가 80,000원\n- 단독 증정: 예약 고객에게 20,000원 상당 케어 제공\n- 적용 기간/수량: 2026년 12월까지, 일 5명 한정",
        helperText: "파란 기본 문구의 대괄호 안을 실제 혜택·조건으로 바꿔주세요.",
        defaultValue: GENERAL_GUIDANCE_DEFAULTS.content.creatripExclusiveBenefit,
        copyKind: "default",
        options: [
          { id: "option.content.creatripExclusiveBenefit.package", label: "단독 패키지 구성", value: "package", description: "크리에이트립에서만 예약할 수 있는 묶음 상품" },
          { id: "option.content.creatripExclusiveBenefit.discount", label: "단독 할인", value: "discount", description: "크리에이트립 예약자에게만 제공하는 할인가" },
          { id: "option.content.creatripExclusiveBenefit.gift", label: "단독 혜택", value: "gift", description: "방문 또는 결제 시 제공하는 증정/서비스 혜택" },
        ],
      },
      { id: "reviewBenefit", path: "content.reviewBenefit", label: "크리에이트립 리뷰작성 혜택", type: "textarea", rows: 3, placeholder: "예: 크리에이트립을 통해 결제 후 리뷰 작성 시 ___상당의 ____를 무료로 받을 수 있습니다. 선착순 조기마감 가능.", helperText: REVIEW_BENEFIT_HELPER_TEXT, copyKind: "helper", defaultHidden: true },
      { id: "clinicStrengths", path: "content.clinicStrengths", label: "병원 특장점", type: "textarea", required: true, rows: 6, placeholder: "예:\n- 000 출신 원장님이 직접 상담·시술\n- 누적 00,000건 이상 진행\n- 내국인 재방문율 00%\n- 관련 수상·인증·방송 출연 이력", helperText: "대괄호 안을 확인 가능한 경력·수치·후기로 바꿔주세요.", defaultValue: GENERAL_GUIDANCE_DEFAULTS.content.clinicStrengths, copyKind: "default" },
      { id: "serviceEnvironment", path: "content.serviceEnvironment", label: "서비스 / 이용환경", type: "textarea", required: true, rows: 6, placeholder: "예:\n- 영어·일본어 통역 스태프 상주\n- 역 1번 출구에서 도보 2분\n- 짐 보관·와이파이·충전 가능\n- 일요일 운영 및 해외카드 결제 가능", helperText: "대괄호 안을 실제 이용 환경으로 바꿔주세요.", defaultValue: GENERAL_GUIDANCE_DEFAULTS.content.serviceEnvironment, copyKind: "default" },
      { id: "referenceLinks", path: "content.referenceLinks", label: "참고하면 좋은 링크", type: "textarea", rows: 3, placeholder: "공식 사이트, 블로그, 보도자료, SNS, 기존 상품/소개 페이지 URL", helperText: `작성된 예약페이지 레퍼런스: ${referenceSpotUrls.join(" / ")}`, copyKind: "reference", defaultHidden: true },
      {
        id: "googleReviewIntegration",
        path: "content.googleReviewIntegration",
        label: "구글맵 리뷰 연동 여부",
        type: "radio",
        required: true,
        defaultHidden: true,
        options: [
          { label: "연동 희망", value: "yes" },
          { label: "연동하지 않음", value: "no" },
        ],
        defaultValue: "yes",
        copyKind: "helper",
      },
      {
        id: "googleBookingIntegration",
        path: "content.googleBookingIntegration",
        label: "구글맵 온라인 예약 버튼 연동 여부",
        type: "radio",
        required: true,
        defaultHidden: true,
        options: [
          { label: "연동 희망", value: "yes" },
          { label: "연동하지 않음", value: "no" },
        ],
        defaultValue: "yes",
        copyKind: "helper",
      },
    ],
    guards: [
      {
        id: "googleBookingOptOutPrompt",
        path: "content.googleBookingIntegration",
        label: "구글 예약 해제 방지 모달",
        description: "파트너가 '연동하지 않음'을 고를 때 전환율 비교 모달로 한 번 확인받습니다.",
        defaultVisibleDomains: ["medical", "beauty"],
      },
    ],
    tips: [
      { title: "좋은 답변은 구체적입니다", body: "‘친절해요’보다 ‘영어 상담 직원 2명이 상주해요’, ‘경험이 많아요’보다 ‘누적 10,000건 이상 진행했어요’처럼 숫자와 실제 사례를 적어주세요.", kind: "success" },
      { title: "고객의 선택 이유를 만들어주세요", body: "누구에게 좋은지 → 무엇을 경험하는지 → 왜 믿을 수 있는지 순서로 2~3문장 이상 작성하면 콘텐츠가 훨씬 풍성해집니다.", kind: "info" },
    ],
  },
  {
    id: "doctors",
    eyebrow: "Step 6",
    title: "의료진 / 전문가 이력을 입력해주세요",
    description: "기존 소개 문구를 붙여넣거나 참고 링크를 남겨주세요.",
    repeaters: [
      {
        id: "doctors",
        path: "doctors",
        label: "의료진 / 전문가",
        description: "이름과 직함은 선택사항입니다. 이력은 한 칸에 편하게 붙여넣거나 링크로 공유해도 됩니다.",
        addLabel: "의료진 추가",
        createItem: createDoctorProfile,
        fields: [
          { id: "name", path: "name", label: "이름", type: "text", placeholder: "예: 김00 원장" },
          { id: "role", path: "role", label: "직함", type: "text", placeholder: "예: 대표원장 / 전문의" },
          { id: "profileText", path: "profileText", label: "이력 / 소개 내용", type: "textarea", rows: 8, placeholder: "예:\n이력\n- 현) 00의원 대표원장\n- 전) 00의원 원장\n\n학력\n- 00대학교 의과대학 졸업\n\n학회\n- 00학회 정회원", helperText: "기존 자료가 있다면 형식 그대로 복사해 붙여넣어도 됩니다. 사진이 있다면 이 의료진 카드의 첨부 영역에 올려주세요.", copyKind: "placeholder" },
          { id: "referenceUrl", path: "referenceUrl", label: "이력 참고 링크", type: "url", placeholder: "https://...", helperText: "공식 홈페이지, 소개 페이지, 프로필 링크가 있으면 입력해주세요." },
        ],
      },
    ],
  },
  {
    id: "policy",
    eyebrow: "Step 7",
    title: "정책과 이용 안내를 확인해주세요",
    description: "파란 기본값이 실제 운영과 다른 곳만 수정해주세요.",
    fields: [
      { id: "cancellationRefundPolicy", path: "policy.cancellationRefundPolicy", label: "취소 및 환불규정", type: "textarea", rows: 5, placeholder: "예:\n방문 3일 전까지 예약 취소 시 100% 환불\n방문 2일 전부터 예약 취소 시 환불 불가\n예약시간에 10분 이상 늦을 경우 예약은 자동 취소되며 결제금액은 환불되지 않습니다.", helperText: "기본 환불규정을 넣어두었습니다. 실제 정책과 다르면 수정해주세요.", defaultValue: "방문 3일 전까지 예약 취소 시 100% 환불\n방문 2일 전부터 예약 취소 시 환불 불가\n예약시간에 10분 이상 늦을 경우 예약은 자동으로 취소되며, 이 경우 결제금액은 환불되지 않습니다.", copyKind: "default" },
      { id: "visitFlow", path: "policy.visitFlow", label: "병원 이용순서", type: "textarea", rows: 7, defaultValue: "1. Creatrip 예약확인증과 여권을 데스크에 제시합니다.\n2. 로비 대기공간에서 대기합니다.\n3. 상담실에서 시술 전 과정과 소요시간을 안내받습니다.\n4. 원장님과 맞춤 상담을 진행합니다.\n5. 안내에 따라 시술을 받습니다.\n6. 시술 후 주의사항을 안내받습니다.", helperText: "기본 이용순서를 넣어두었습니다. 실제 방문 흐름과 다르면 수정해주세요.", copyKind: "default" },
      {
        id: "bookingQuestionChecklist",
        path: "policy.bookingQuestionChecklist",
        label: "예약 시 고객에게 받을 정보",
        type: "multiCheckbox",
        helperText: getBookingQuestionChecklistHelperText("해당 카테고리"),
        options: [
          { label: "예약자 여권 영문 이름", value: "예약자 여권 영문 이름" },
          { label: "생년월일", value: "생년월일" },
          { label: "성별", value: "성별" },
          { label: "국적", value: "국적" },
          { label: "여권번호", value: "여권번호" },
          { label: "SNS 채널", value: "SNS 채널", description: "Kakao, Line, WhatsApp, WeChat 등" },
          { label: "SNS ID", value: "SNS ID" },
          { label: "Line ID QR코드", value: "Line ID QR코드", description: "Line 앱의 내 QR코드 이미지입니다. ID 검색이 안 될 때 친구 추가용으로 사용합니다." },
          { label: "한국 내 연락 가능한 번호", value: "한국 내 연락 가능한 번호" },
          { label: "상담/시술 희망 내용", value: "상담/시술 희망 내용" },
          { label: "개선 원하는 부위와 증상", value: "개선 원하는 부위와 증상" },
          { label: "원하는 후보 날짜/시간대", value: "원하는 후보 날짜/시간대" },
          { label: "방문/시술 인원 수", value: "방문/시술 인원 수" },
          { label: "복용약·질환·임신 여부", value: "복용약·질환·임신 여부" },
          { label: "알레르기 여부", value: "알레르기 여부" },
          { label: "초진/재진 여부", value: "초진/재진 여부" },
          { label: "통역 필요 여부/희망 언어", value: "통역 필요 여부/희망 언어" },
          { label: "입국일/출국 예정일", value: "입국일/출국 예정일" },
        ],
        defaultValue: "예약자 여권 영문 이름\n생년월일\n성별\n국적\nSNS 채널\nSNS ID\n상담/시술 희망 내용\n개선 원하는 부위와 증상\n복용약·질환·임신 여부",
        copyKind: "default",
      },
      { id: "bookingQuestions", path: "policy.bookingQuestions", label: "기타로 받고 싶은 정보", type: "textarea", rows: 5, placeholder: "체크리스트에 없는 질문이 있다면 한 줄에 하나씩 적어주세요.\n예:\n평소 치아/피부 고민이 있다면 적어주세요.\n원하는 상담 또는 시술 부위를 알려주세요.\n방문 전 파트너에게 전달할 요청사항이 있나요?", copyKind: "placeholder", defaultHidden: true },
      {
        id: "consultationFeePolicy",
        path: "policy.consultationFeePolicy",
        label: "시술 미진행 시 상담비",
        type: "radio",
        required: true,
        defaultValue: "not_charged",
        options: [
          { label: "부과 안 함", value: "not_charged", description: "상담 후 시술을 진행하지 않아도 별도 상담비를 받지 않습니다." },
          { label: "부과함", value: "charged", description: "상담만 진행하고 시술하지 않는 경우 상담비를 받습니다." },
        ],
      },
      {
        id: "consultationFeeAmount",
        path: "policy.consultationFeeAmount",
        label: "시술 미진행 시 상담비 (KRW)",
        type: "currency",
        placeholder: "예: 20,000원",
        helperText: "시술을 진행하지 않고 상담만 받을 때 부과되는 금액을 원화로 입력해주세요.",
      },
      {
        id: "minorPolicy",
        path: "policy.minorPolicy",
        label: "미성년자 이용 가능 여부",
        type: "radio",
        defaultHidden: true,
        options: [
          { label: "보호자 동의 또는 동반 필요", value: "보호자 동의 또는 동반 필요" },
          { label: "예약 가능", value: "예약 가능" },
          { label: "예약 불가", value: "예약 불가" },
          { label: "상품/서비스별 상이", value: "상품/서비스별 상이" },
        ],
        defaultValue: "보호자 동의 또는 동반 필요",
        copyKind: "default",
      },
      {
        id: "passportNeeded",
        path: "policy.passportNeeded",
        label: "여권 또는 신분증 필요 여부",
        type: "radio",
        defaultHidden: true,
        options: [
          { label: "방문 시 여권 또는 외국인등록증 지참 필요", value: "방문 시 여권 또는 외국인등록증 지참 필요" },
          { label: "필요 없음", value: "필요 없음" },
          { label: "상품/서비스별 상이", value: "상품/서비스별 상이" },
        ],
        defaultValue: "방문 시 여권 또는 외국인등록증 지참 필요",
        copyKind: "default",
      },
      {
        id: "healthRestrictions",
        path: "policy.healthRestrictions",
        label: "사전 확인이 필요한 건강 상태",
        type: "multiCheckbox",
        helperText: "해당되는 항목을 선택해주세요. 자세한 내용은 주의사항에 적으면 됩니다.",
        options: [
          { label: "임산부 사전 상담 필요", value: "임산부 사전 상담 필요" },
          { label: "특정 질환자 사전 상담 필요", value: "특정 질환자 사전 상담 필요" },
          { label: "복용약이 있으면 사전 고지 필요", value: "복용약이 있으면 사전 고지 필요" },
          { label: "시술별 제한사항 있음", value: "시술별 제한사항 있음" },
          { label: "특별 제한 없음", value: "특별 제한 없음" },
        ],
        defaultValue: "임산부 사전 상담 필요\n특정 질환자 사전 상담 필요\n복용약이 있으면 사전 고지 필요",
        copyKind: "default",
      },
      { id: "otherPrecautions", path: "policy.otherPrecautions", label: "주의사항", type: "textarea", rows: 8, placeholder: "예약 및 방문, 전액 결제/부가세/세금 환급 여부, 시술 후 관리, 부작용, 피해야 할 음식이나 행동 등을 최대한 상세히 적어주세요.\n\n예: 미백 시술 후 2시간 동안 식사를 삼가고 흡연이나 색소가 많은 음식은 피해주세요. 미백 효과는 영구적이지 않으므로 주기적 관리가 중요합니다.", helperText: "대괄호 안을 실제 예약·방문·관리 정책으로 바꿔주세요.", defaultValue: GENERAL_GUIDANCE_DEFAULTS.policy.otherPrecautions, copyKind: "default" }
    ],
  },
  {
    id: "photos",
    eyebrow: "Step 8",
    title: "사진을 첨부해주세요",
    description: "상품 페이지에 사용할 가로형 고해상도 사진을 첨부해주세요.",
    fileUploads: PHOTO_UPLOADS,
    fields: [
      { id: "imageUsageConsent", path: "documents.imageUsageConsent", label: "첨부한 이미지를 크리에이트립 상품페이지 및 홍보 콘텐츠 제작에 사용하는 것에 동의합니다.", type: "checkbox", required: true, helperText: "저작권/초상권 등 사용 권한이 있는 이미지만 첨부해주세요.", copyKind: "helper" },
    ],
    tips: [
      { title: "가로형 사진을 권장합니다", body: "상품 대표사진과 콘텐츠 영역에서 안정적으로 보이도록 가로형 고해상도 사진을 권장합니다.", kind: "info" },
    ],
  },
  {
    id: "documents",
    eyebrow: "Step 9",
    title: "입점 서류를 첨부해주세요",
    description: "입점 검토와 거래처 등록에 필요한 서류입니다. 통장 사본은 첫 정산 때 제출해도 됩니다.",
    fields: [
      { id: "documentAccuracyConsent", path: "documents.documentAccuracyConsent", label: "제출한 정보와 서류가 정확함을 확인했습니다.", type: "checkbox", required: true },
    ],
    fileUploads: DOCUMENT_UPLOADS,
  },
  {
    id: "review",
    eyebrow: "Step 10",
    title: "제출 전 확인해주세요",
    description: "입력한 정보와 첨부 자료를 확인한 뒤 제출해주세요.",
  },
];
